import React from 'react';
import { Radio, Checkbox, InputNumber } from 'antd';
import type { F113 } from '../../../services/p1113Api';
import LookupField from '../../../components/LookupField';

// 產品類型選項 (BR-07, ui_spec page1 f113_29)
const productTypes = [
  { value: 1, label: '原料' },
  { value: 2, label: '色布' },
  { value: 3, label: '原料(皮)' },
  { value: 4, label: '服裝' },
  { value: 5, label: '原料(膜/膠)' },
  { value: 6, label: '胚布' },
];

// 產品類型細項 (BR-07)
const subtypeMap: Record<number, { value: number; label: string }[]> = {
  2: [{ value: 1, label: '梭織布' }, { value: 2, label: '針織布' }, { value: 3, label: '貼合布' }],
  5: [{ value: 4, label: '膠' }, { value: 5, label: '膜' }],
  6: [{ value: 1, label: '梭織布' }, { value: 2, label: '針織布' }],
};

interface Props {
  formData: Partial<F113>;
  isEditing: boolean;
  mode: string;
  getField: (f: keyof F113) => unknown;
  setField: (f: keyof F113, v: unknown) => void;
}

const Tab1ProductSpec: React.FC<Props> = React.memo(({ formData, isEditing, mode, getField, setField }) => {
  const prodType = Number(getField('f113_29')) || 0;
  const showSubtype = [2, 5, 6].includes(prodType);
  const subtypeOptions = subtypeMap[prodType] || [];

  const handleTypeChange = (val: number) => {
    setField('f113_29', val);
    // BR-07: clear subtype if not applicable
    if (![2, 5, 6].includes(val)) {
      setField('f113_46', 0);
    }
  };

  return (
    <div className="p1113-form-grid">
      {/* Left Column */}
      <div className="p1113-col">
        <div className="p1113-field-row">
          <label>產品名稱 <span className="required">*</span></label>
          <textarea
            value={String(getField('f113_11') || '')}
            onChange={e => setField('f113_11', e.target.value)}
            disabled={!isEditing}
            className="p1113-textarea"
            rows={2}
          />
        </div>
        <div className="p1113-field-row">
          <label>產品規格</label>
          <textarea
            value={String(getField('f113_21') || '')}
            onChange={e => setField('f113_21', e.target.value)}
            disabled={!isEditing}
            className="p1113-textarea"
            rows={2}
          />
        </div>
        <div className="p1113-field-row">
          <label>加工別</label>
          <textarea
            value={String(getField('f113_20') || '')}
            onChange={e => setField('f113_20', e.target.value)}
            disabled={!isEditing}
            className="p1113-textarea"
            rows={2}
          />
        </div>
        <div className="p1113-field-row">
          <label>針孔內</label>
          <div className="p1113-input-group">
            <InputNumber
              value={Number(getField('f113_22')) || undefined}
              onChange={v => setField('f113_22', v ?? 0)}
              disabled={!isEditing}
              step={0.1}
              style={{ width: 100 }}
            />
            <span className="p1113-unit">INCH</span>
          </div>
        </div>
        <div className="p1113-field-row">
          <label>針孔外</label>
          <InputNumber
            value={Number(getField('f113_26')) || undefined}
            onChange={v => setField('f113_26', v ?? 0)}
            disabled={!isEditing}
            step={0.1}
            style={{ width: 100 }}
          />
        </div>
        <div className="p1113-field-row">
          <label>重量</label>
          <InputNumber
            value={Number(getField('f113_24')) || undefined}
            onChange={v => setField('f113_24', v ?? 0)}
            disabled={!isEditing}
            step={0.1}
            style={{ width: 100 }}
          />
        </div>
        <div className="p1113-field-row">
          <label>米平方重</label>
          <InputNumber
            value={Number(getField('f113_241')) || undefined}
            onChange={v => setField('f113_241', v ?? 0)}
            disabled={!isEditing}
            step={0.1}
            style={{ width: 100 }}
          />
        </div>
        <div className="p1113-field-row">
          <label>最近採購單價</label>
          <InputNumber
            value={Number(getField('f113_15')) || undefined}
            disabled
            style={{ width: 140 }}
            formatter={v => v ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : ''}
          />
        </div>
        <div className="p1113-field-row">
          <label>最近異動日</label>
          <input
            value={String(getField('f113_13') || '-').toString().substring(0, 10)}
            disabled
            className="p1113-input w-100"
          />
        </div>
      </div>

      {/* Right Column */}
      <div className="p1113-col">
        <div className="p1113-field-row">
          <label>單位類別</label>
          <LookupField
            lookupType="unitCategory"
            value={String(getField('f113_19') || '')}
            onChange={(code) => setField('f113_19', code)}
            disabled={!isEditing}
            style={{ width: 60 }}
            title="搜尋單位類別"
          />
          <label className="p1113-label-inline">單位 <span className="required">*</span></label>
          <input
            value={String(getField('f113_12') || '')}
            onChange={e => setField('f113_12', e.target.value)}
            maxLength={3}
            disabled={!isEditing}
            className="p1113-input w-80"
          />
        </div>
        <div className="p1113-field-row">
          <label>產品類別</label>
          <input
            value={String(getField('f113_27') || '')}
            onChange={e => setField('f113_27', e.target.value)}
            maxLength={40}
            disabled={!isEditing}
            className="p1113-input w-100"
          />
        </div>
        <div className="p1113-field-row">
          <label>產品全名</label>
          <textarea
            value={String(getField('f113_30') || '')}
            disabled  /* BR-26: F113_30 always readonly */
            className="p1113-textarea p1113-readonly"
            rows={3}
          />
        </div>
        <div className="p1113-field-row">
          <label>產品類型 <span className="required">*</span></label>
          <Radio.Group
            value={prodType || undefined}
            onChange={e => handleTypeChange(e.target.value)}
            disabled={!isEditing}
          >
            {productTypes.map(o => (
              <Radio key={o.value} value={o.value}>{o.label}</Radio>
            ))}
          </Radio.Group>
        </div>
        {showSubtype && (
          <div className="p1113-field-row">
            <label>類型細項</label>
            <Radio.Group
              value={Number(getField('f113_46')) || undefined}
              onChange={e => setField('f113_46', e.target.value)}
              disabled={!isEditing}
            >
              {subtypeOptions.map(o => (
                <Radio key={o.value} value={o.value}>{o.label}</Radio>
              ))}
            </Radio.Group>
          </div>
        )}
        <div className="p1113-field-row">
          <Checkbox
            checked={Boolean(getField('f113_31'))}
            onChange={e => setField('f113_31', e.target.checked)}
            disabled={!isEditing}
          >
            舊代號
          </Checkbox>
          <Checkbox
            checked={Boolean(getField('f113_37'))}
            onChange={e => setField('f113_37', e.target.checked)}
            disabled={!isEditing}
            style={{ marginLeft: 16 }}
          >
            {/* f113_37 unnamed checkbox from FoxPro */}
          </Checkbox>
        </div>
        <div className="p1113-field-row">
          <label>#</label>
          <InputNumber
            value={Number(getField('f113_38')) || undefined}
            onChange={v => setField('f113_38', v ?? 0)}
            disabled={!isEditing}
            step={0.1}
            style={{ width: 100 }}
          />
        </div>
      </div>
    </div>
  );
});

Tab1ProductSpec.displayName = 'Tab1ProductSpec';
export default Tab1ProductSpec;
